import { useMemo, useState } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';

import { Button, Flex, Text } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import {
  Campaign,
  CampaignStatus,
  CupOutline,
  DropdownOption,
  Filters,
  PageEmptyState,
  useToast,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import { Table } from 'app/components/UI/Table/Table';
import getCampaignColumns, { campaignStatusDetails } from 'app/utils/table/campaigns';
import useCreateCampaign from 'app/hooks/useCreateCampaign';
import useFetchCampaigns from 'app/hooks/useFetchCampaigns';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useFetchIndividuals from 'app/hooks/useFetchIndividuals';
import usePublishCampaign from 'app/hooks/usePublishCampaign';
import useUnpublishCampaign from 'app/hooks/useUnpublishCampaign';
import { stateMoveProblem } from 'app/utils/campaigns/stateMove';
import useForecastSchedule from 'app/hooks/useForecastSchedule';
import useGroups from 'app/hooks/useGroups';
import { companyPaths } from 'app/constants/routes';
import { useManagingCompanyStore } from 'app/store';

import CreateCampaignForm from './CreateCampaign/CreateCampaignForm';
import ScheduleForecast from './ScheduleForecast';
import CancelCampaignModal from './CancelCampaignModal';
import EditCampaignModal from './CampaignDetail/EditCampaignModal';
import { useCreateCampaignForm } from './CreateCampaign/useCreateCampaignForm';

import type { Filter, FilterData, MultiSelectionData } from '@aslanpay/portal-common-lib';

import type { PaginationState } from '@tanstack/react-table';

/** The one filter this listing offers, named once so the handler and the control cannot drift apart. */
const STATUS_FILTER = 'State';

const campaignStatusOptions: DropdownOption[] = Object.entries(campaignStatusDetails).map(
  ([value, details]) => ({ value, text: details.label })
);

function Campaigns() {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const company = useManagingCompanyStore((state) => state.company);
  const companyId = company?.id || '';

  const { isInitialLoading } = useFetchCompanyConfiguration(companyId);

  const [isCreating, setIsCreating] = useState(false);
  // The campaign a row's action is about. Held rather than passed, so the modal outlives the menu closing.
  const [editing, setEditing] = useState<Campaign>();
  const [cancelling, setCancelling] = useState<Campaign>();

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [statusFilter, setStatusFilter] = useState<CampaignStatus[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const filters: Filter[] = [
    {
      name: STATUS_FILTER,
      type: 'category',
      data: campaignStatusOptions,
      selectedValues: statusFilter,
      singleSelection: true,
    },
  ];

  const isFiltered = statusFilter.length > 0;

  const handleFilterChange = (filterName: string, newValues: string[]) => {
    if (filterName !== STATUS_FILTER) {
      return;
    }

    // One state at a time, so the last thing picked is the filter.
    setStatusFilter(newValues.slice(-1) as CampaignStatus[]);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) =>
    (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
    String(filterValue);

  const {
    data: campaignsPage,
    isInitialLoading: isLoadingCampaigns,
    isError: campaignsFailed,
    refetch: refetchCampaigns,
  } = useFetchCampaigns({
    companyId,
    status: statusFilter[0],
    page: pagination.pageIndex,
    size: pagination.pageSize,
  });
  const campaigns = campaignsPage?.list ?? [];
  const { mutate: publish } = usePublishCampaign({
    onSuccess: (campaign) => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Campaign published',
        description: `${campaign.name} will open for nominations on schedule.`,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not publish the campaign',
        description: stateMoveProblem(error),
      }),
  });

  const { mutate: unpublish } = useUnpublishCampaign({
    onSuccess: (campaign) => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Campaign back to draft',
        description: `${campaign.name} will not open until it is published again.`,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not put the campaign back to a draft',
        description: stateMoveProblem(error),
      }),
  });

  const columns = useMemo(
    () =>
      getCampaignColumns(
        (campaign) =>
          navigate(
            generatePath(companyPaths.campaignDetail.path, { companyId, campaignId: campaign.id })
          ),
        {
          view: (campaign) =>
            navigate(
              generatePath(companyPaths.campaignDetail.path, { companyId, campaignId: campaign.id })
            ),
          edit: setEditing,
          publish: (campaign) => publish(campaign.id),
          unpublish: (campaign) => unpublish(campaign.id),
          cancel: setCancelling,
        }
      ),
    [companyId, navigate, publish, unpublish]
  );

  const { data: groupsPage } = useGroups.useFetchGroups(
    { companyId, page: 0, size: 9999 },
    !!companyId && isCreating
  );
  const prides = useMemo(
    () => (groupsPage?.list ?? []).map((group) => ({ id: group.id, name: group.name })),
    [groupsPage]
  );

  const [candidateQuery, setCandidateQuery] = useState('');

  // Only asked for while the shortlist is being built, which is the one place a candidate is chosen.
  const { data: candidatePool } = useFetchIndividuals({
    companyId: [companyId],
    role: ['EMPLOYEE'],
    query: candidateQuery,
    page: 0,
    size: 20,
    enabled: isCreating,
  });

  const candidates = useMemo(
    () =>
      (candidatePool?.list ?? []).map((person) => ({
        value: person.id,
        text: `${person.firstName} ${person.lastName}`,
        jobTitle: person.jobTitle,
      })),
    [candidatePool]
  );

  const { form, setField, reset, fansOut, errorFor, submit, request, forecastRequest } =
    useCreateCampaignForm(companyId);

  const {
    data: forecast,
    isFetching: isForecasting,
    isError: forecastFailed,
  } = useForecastSchedule(isCreating ? forecastRequest : undefined);
  const { mutateAsync: createCampaignAsync, isLoading: isCreatingCampaign } = useCreateCampaign({});

  const handleCreate = async () => {
    if (!submit()) return;

    try {
      const campaign = await createCampaignAsync(request);

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Campaign created',
        description: `${campaign.name} is set up and will open for nominations on schedule.`,
      });

      reset();
      setIsCreating(false);
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not create the campaign',
        description: 'Nothing was saved. Check the details and try again.',
      });
    }
  };

  const handleCancel = () => {
    reset();
    setIsCreating(false);
  };

  if (isInitialLoading) {
    return (
      <AuthenticatedPageWrapper title="Campaigns">
        <CenterSpinner />
      </AuthenticatedPageWrapper>
    );
  }

  return (
    <AuthenticatedPageWrapper title="Campaigns">
      <Flex flexDir="column" gap="24px">
        <Flex align="flex-start" justify="space-between" gap="15px">
          <Flex flexDir="column" gap="4px">
            <Text as="h2" textStyle="h2" color="brand.darkPurple">
              Campaigns
            </Text>
            <Text color="brand.darkPurple">
              Set up and run recurring award campaigns across this company&apos;s prides
            </Text>
          </Flex>
          {!isCreating && (
            <Button onClick={() => setIsCreating(true)} data-testid="create-campaign-button">
              Create campaign
            </Button>
          )}
        </Flex>

        {isCreating && (
          <CreateCampaignForm
            form={form}
            setField={setField}
            prides={prides}
            candidates={candidates}
            onSearchCandidates={setCandidateQuery}
            fansOut={fansOut}
            forecast={
              form.recurrence.recurring ? (
                <ScheduleForecast
                  cycles={forecast ?? []}
                  isLoading={isForecasting}
                  isError={forecastFailed}
                  needsSchedule={!forecastRequest}
                />
              ) : null
            }
            errorFor={errorFor}
            isSaving={isCreatingCampaign}
            onSubmit={handleCreate}
            onCancel={handleCancel}
          />
        )}

        {!isCreating && (
          <Flex align="center" gap="10px">
            <Filters
              activeFilter={activeFilter}
              displayFormatter={formatFilterValue}
              filters={filters}
              onFilterClose={() => setActiveFilter(undefined)}
              onFilterChange={handleFilterChange}
              onFilterClick={setActiveFilter}
            />
          </Flex>
        )}

        {!isCreating && isLoadingCampaigns && <CenterSpinner />}

        {!isCreating && !isLoadingCampaigns && !campaignsFailed && campaigns.length > 0 && (
          <Flex flexDir="column" data-testid="campaigns-table">
            <Table
              columns={columns}
              data={campaigns}
              getRowId={(campaign) => campaign.id}
              pageNumber={campaignsPage?.pageNumber ?? pagination.pageIndex}
              pageSize={campaignsPage?.pageSize ?? pagination.pageSize}
              total={campaignsPage?.total ?? campaigns.length}
              sorting={[]}
              onPaginationChange={setPagination}
              onSortingChange={() => {}}
            />
          </Flex>
        )}

        {!isCreating && !isLoadingCampaigns && campaignsFailed && (
          <PageEmptyState
            icon={<CupOutline color="text.error" boxSize="40px" />}
            title="We couldn't load the campaigns"
            description="Nothing has been lost. Check your connection and try again."
            ctaLabel="Try again"
            onCtaClick={() => refetchCampaigns()}
          />
        )}

        {!isCreating && !isLoadingCampaigns && !campaignsFailed && campaigns.length === 0 && (
          <PageEmptyState
            icon={<CupOutline color="brand.purple" boxSize="40px" />}
            title={isFiltered ? 'No campaigns found' : 'No campaigns yet'}
            description={
              isFiltered
                ? 'No campaign is in that state. Try another one, or clear the filter.'
                : 'Campaigns you create will appear here.'
            }
          />
        )}
      </Flex>

      {editing && (
        <EditCampaignModal isOpen onClose={() => setEditing(undefined)} campaign={editing} />
      )}
      {cancelling && (
        <CancelCampaignModal
          isOpen
          onClose={() => setCancelling(undefined)}
          campaignId={cancelling.id}
          title="Cancel this campaign?"
          summary="Every pride of this campaign still running stops. A pride that has already finished is left as it is."
          successDescription={`${cancelling.name} has stopped. Its nominations and any decision have been kept.`}
        />
      )}
    </AuthenticatedPageWrapper>
  );
}

export default Campaigns;
